// src/pages/HomePage.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { auth, db } from "../firebase";
import Layout from "../components/Layout";
import { useStudent } from "../hooks/useStudent";
import { registerForNotifications, onForegroundMessage } from "../utils/notifications";
import { MUSIC_TRIVIA } from "../data/musicTrivia";
import { useTranslation } from "react-i18next";
import { translateCourseName } from "../utils/musicTerms";
import { SCHOOL } from "../config";

function subjectEmoji(s) {
  return { Vocal:"🎤", Harmonium:"🎹", Tabla:"🥁", Sitar:"🎸", Flute:"🎵" }[s] || "🎼";
}
function isMobile() { return window.innerWidth <= 900; }

// ── Contact footer ────────────────────────────────────────────────────────────
function ContactFooter({ compact }) {
  if (compact) {
    // Mobile: stacked, medium-purple gradient, full details
    return (
      <div style={{
        background: "linear-gradient(135deg,#7B1515 0%,#8B3FCC 50%,#9B2020 100%)",
        borderRadius: "16px",
        padding: "16px 18px",
        color: "white",
        flex: 5,
        minHeight: 0,
        width: "100%",
        boxSizing: "border-box",
        boxShadow: "0 4px 20px rgba(123,21,21,.25)",
      }}>
        {/* Brand */}
        <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"10px" }}>
          <picture>
            <source srcSet="/logo.webp" type="image/webp" />
            <img src="/logo.png" alt={SCHOOL.name}
              style={{ width:"36px", height:"36px", objectFit:"contain",
                       borderRadius:"6px", background:"rgba(255,255,255,.15)", padding:"3px" }}/>
          </picture>
          <div>
            <div style={{ fontWeight:"800", fontSize:".95rem", letterSpacing:".2px" }}>
              {SCHOOL.name}
            </div>
            <div style={{ fontSize:".72rem", opacity:.75, fontStyle:"italic" }}>
              {SCHOOL.tagline}
            </div>
          </div>
        </div>
        {/* Divider */}
        <div style={{ height:"1px", background:"rgba(255,255,255,.2)",
                      marginBottom:"10px" }}/>
        {/* Contact */}
        <div style={{ fontSize:".78rem", opacity:.88, lineHeight:1.85 }}>
          <div>📞 {SCHOOL.phone}</div>
          <div>📧 {SCHOOL.email}</div>
          <div>📍 {SCHOOL.address}</div>
        </div>
      </div>
    );
  }
  // Desktop
  return (
    <div style={{
      background: "linear-gradient(135deg,#4E0808 0%,#7B1515 60%,#9B2020 100%)",
      borderRadius: "14px",
      padding: "20px 28px",
      color: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "12px",
      boxShadow: "0 4px 20px rgba(123,21,21,.2)",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
        <picture>
          <source srcSet="/logo.webp" type="image/webp" />
          <img src="/logo.png" alt={SCHOOL.name}
            style={{ width:"44px", height:"44px", objectFit:"contain",
                     borderRadius:"8px", background:"rgba(255,255,255,.15)", padding:"4px" }}/>
        </picture>
        <div>
          <div style={{ fontWeight:"800", fontSize:"1.05rem" }}>
            {SCHOOL.name}
          </div>
          <div style={{ fontSize:".8rem", opacity:.7, marginTop:"3px", fontStyle:"italic" }}>
            {SCHOOL.tagline}
          </div>
        </div>
      </div>
      <div style={{ fontSize:".82rem", opacity:.85, lineHeight:1.9 }}>
        <div>📞 {SCHOOL.phone}</div>
        <div>📧 {SCHOOL.email}</div>
        <div>📍 {SCHOOL.address}</div>
      </div>
    </div>
  );
}

// ── Trivia card with < > navigation + crossfade animation ────────────────────
function TriviaCard({ fact, index, total, onPrev, onNext, compact, visible }) {
  const { t } = useTranslation();
  const didYouKnow = t("home.didYouKnow");
  const btnSize = compact ? "34px" : "40px";
  return (
    <div style={{
      background: "var(--surface-2,linear-gradient(135deg,#FBF0E8 0%,#EDE9FE 60%,#F5E4D0 100%))",
      border: "1.5px solid var(--border-warm,#D8B4FE)",
      borderRadius: "14px",
      position: "relative",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      overflow: "hidden",
      padding: compact ? "12px 50px" : "20px 60px",
      flex: compact ? 7 : undefined,
      minHeight: 0,
      width: "100%",
      boxSizing: "border-box",
    }}>
      {/* Decorative watermark */}
      <div style={{
        position:"absolute", right:"-4px", bottom:"-10px",
        fontSize:"5.5rem", opacity:.08, lineHeight:1,
        userSelect:"none", pointerEvents:"none", color:"var(--purple-text)",
      }}>🎵</div>

      {/* Animated content wrapper — crossfade on fact change */}
      <div style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(6px)",
        transition: "opacity .28s ease, transform .28s ease",
      }}>
        {/* Label */}
        <div style={{
          fontSize:".63rem", fontWeight:"700", letterSpacing:"1.2px",
          color:"#7C3AED", textTransform:"uppercase", marginBottom:"7px",
        }}>
          🎵 {didYouKnow}
        </div>

        {/* Fact text */}
        <p style={{
          fontSize: compact ? ".9rem" : ".92rem",
          lineHeight: 1.65,
          color: "var(--text)",
          margin: 0,
          display: "-webkit-box",
          WebkitLineClamp: compact ? 5 : 8,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>
          {fact}
        </p>

      </div>

      {/* ‹ Prev button */}
      <button
        onClick={onPrev}
        aria-label="Previous fact"
        style={{
          position:"absolute", left:"5px", top:"50%",
          transform:"translateY(-50%)",
          width:btnSize, height:btnSize,
          background:"rgba(109,40,217,.12)",
          border:"none", borderRadius:"50%",
          cursor:"pointer", fontSize:"1.15rem",
          color:"var(--purple-text)", display:"flex",
          alignItems:"center", justifyContent:"center",
          transition:"background .15s",
          flexShrink:0,
        }}
        onMouseEnter={e=>e.currentTarget.style.background="rgba(109,40,217,.22)"}
        onMouseLeave={e=>e.currentTarget.style.background="rgba(109,40,217,.12)"}
      >‹</button>

      {/* › Next button */}
      <button
        onClick={onNext}
        aria-label="Next fact"
        style={{
          position:"absolute", right:"5px", top:"50%",
          transform:"translateY(-50%)",
          width:btnSize, height:btnSize,
          background:"rgba(109,40,217,.12)",
          border:"none", borderRadius:"50%",
          cursor:"pointer", fontSize:"1.15rem",
          color:"var(--purple-text)", display:"flex",
          alignItems:"center", justifyContent:"center",
          transition:"background .15s",
          flexShrink:0,
        }}
        onMouseEnter={e=>e.currentTarget.style.background="rgba(109,40,217,.22)"}
        onMouseLeave={e=>e.currentTarget.style.background="rgba(109,40,217,.12)"}
      >›</button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function HomePage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { student, course, batch, loading } = useStudent();
  const [toast,          setToast]          = useState(null);
  const [mobile,         setMobile]         = useState(isMobile());
  const [showHistory,    setShowHistory]    = useState(false);
  // Trivia state
  const [triviaList,  setTriviaList]  = useState(MUSIC_TRIVIA);
  const [triviaIndex, setTriviaIndex] = useState(
    () => Math.floor(Math.random() * MUSIC_TRIVIA.length)
  );

  useEffect(() => {
    const onResize = () => setMobile(isMobile());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (uid) registerForNotifications(uid);
  }, []);

  useEffect(() => {
    const unsub = onForegroundMessage((payload) => {
      const title = payload.notification?.title || SCHOOL.name;
      const body  = payload.notification?.body  || "";
      setToast({ title, body });
      setTimeout(() => setToast(null), 6000);
    });
    return unsub;
  }, []);

  // Try to fetch custom trivia from Firestore; fall back to built-in list
  useEffect(() => {
    getDoc(doc(db, "config", "musicTrivia"))
      .then(snap => {
        const remote = snap.exists() && Array.isArray(snap.data().facts) && snap.data().facts.length > 0
          ? snap.data().facts
          : MUSIC_TRIVIA;
        setTriviaList(remote);
        setTriviaIndex(Math.floor(Math.random() * remote.length));
      })
      .catch(() => {});
  }, []);

  // Crossfade animation state
  const [triviaVisible,  setTriviaVisible]  = useState(true);
  const [displayIndex,   setDisplayIndex]   = useState(triviaIndex);
  const [displayFact,    setDisplayFact]    = useState(() => triviaList[triviaIndex] ?? triviaList[0]);

  // Animate fact change: fade out → swap → fade in
  const changeTo = useCallback((newIndex) => {
    setTriviaVisible(false);
    setTimeout(() => {
      setDisplayIndex(newIndex);
      setDisplayFact(triviaList[newIndex] ?? triviaList[0]);
      setTriviaVisible(true);
    }, 280);
  }, [triviaList]);

  const prevTrivia = useCallback(() => {
    const next = (triviaIndex - 1 + triviaList.length) % triviaList.length;
    setTriviaIndex(next);
    changeTo(next);
  }, [triviaIndex, triviaList.length, changeTo]);

  const nextTrivia = useCallback(() => {
    const next = (triviaIndex + 1) % triviaList.length;
    setTriviaIndex(next);
    changeTo(next);
  }, [triviaIndex, triviaList.length, changeTo]);

  // Sync display when trivia list loads from Firestore
  useEffect(() => {
    setDisplayFact(triviaList[triviaIndex] ?? triviaList[0]);
    setDisplayIndex(triviaIndex);
  }, [triviaList]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentFact = displayFact;

  const toastEl = toast && (
    <div onClick={() => setToast(null)} style={{
      position:"fixed", top:"16px", left:"50%", transform:"translateX(-50%)",
      zIndex:9999, background:"#7B1515", color:"white",
      borderRadius:"12px", padding:"14px 20px", maxWidth:"360px", width:"90%",
      boxShadow:"0 8px 24px rgba(0,0,0,.25)", cursor:"pointer",
    }}>
      <div style={{ fontWeight:"700", fontSize:".95rem" }}>🔔 {toast.title}</div>
      {toast.body && <div style={{ fontSize:".85rem", marginTop:"4px", opacity:.9 }}>{toast.body}</div>}
    </div>
  );

  if (loading) return (
    <Layout title={t("home.title")} student={null}>
      <div className="empty-state">
        <div className="icon">🎵</div>
        <p>{t("home.loadingDashboard")}</p>
      </div>
    </Layout>
  );

  const history = student?.courseHistory || [];

  // ── MOBILE VIEW ──────────────────────────────────────────────────────────────
  // page-body is now the scroll container (height = 100dvh - header - bottom-nav).
  // We fill it with height:100% so no scrolling on the homepage.
  if (mobile) {
    return (
      <Layout title={t("home.title")} student={student}>
        {toastEl}

        {/* Course history bottom sheet (mobile) */}
        {showHistory && (
          <div onClick={() => setShowHistory(false)} style={{
            position:"fixed", inset:0, zIndex:9000,
            background:"rgba(0,0,0,.45)", display:"flex",
            alignItems:"flex-end",
          }}>
            <div onClick={e => e.stopPropagation()}
              onTouchMove={e => e.stopPropagation()}
              style={{
              background:"var(--surface,white)", borderRadius:"20px 20px 0 0",
              padding:"20px 20px 32px", width:"100%", maxHeight:"70vh",
              overflowY:"scroll", WebkitOverflowScrolling:"touch",
            }}>
              <div style={{ width:"36px", height:"4px", background:"#D1D5DB",
                            borderRadius:"2px", margin:"0 auto 16px" }}/>
              <div style={{ fontWeight:"800", fontSize:"1rem",
                            color:"var(--purple-text)", marginBottom:"14px" }}>
                📜 {t("home.courseHistory")}
              </div>
              {[...history].reverse().map((h, i) => (
                <div key={i} style={{
                  background:"var(--surface-2,#F9F5FF)",
                  borderRadius:"12px", padding:"13px 14px",
                  marginBottom:"10px",
                  border:"1px solid var(--border-warm,#F5E4D0)",
                }}>
                  <div style={{ fontWeight:"700", fontSize:".9rem",
                                color:"var(--text)", marginBottom:"2px" }}>
                    {h.courseName||"—"}
                  </div>
                  <div style={{ fontSize:".77rem", color:"var(--text-muted)",
                                marginBottom:"8px" }}>
                    🕐 {h.batchName||"—"} &nbsp;·&nbsp; ✅ {h.promotedAt||"—"}
                    {h.subject && <span> &nbsp;·&nbsp; {h.subject}</span>}
                    {h.notes && <div style={{ marginTop:"2px", fontStyle:"italic" }}>"{h.notes}"</div>}
                  </div>
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ width:"100%" }}
                    onClick={() => {
                      setShowHistory(false);
                      navigate(`/course/${h.courseId}`,
                        { state:{ batchId:h.batchId, isCompleted:true } });
                    }}>
                    {t("home.viewHistory")}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Fill 100% of the page-body content area — no scroll needed */}
        <div style={{
          display:"flex", flexDirection:"column", gap:"10px",
          height:"100%", minHeight:0, width:"100%",
        }}>

          {/* ── My Current Course card ── */}
          <div style={{
            background:"var(--surface,white)", borderRadius:"16px", padding:"16px",
            flexShrink:0, width:"100%", boxSizing:"border-box",
            boxShadow:"0 2px 12px rgba(123,21,21,.10)",
            border:"1px solid var(--border-warm,#F5E4D0)",
            position:"relative", overflow:"hidden",
          }}>
            {/* Decorative music note watermarks */}
            <div style={{
              position:"absolute", right:"-6px", top:"-6px",
              fontSize:"5.5rem", opacity:.055, lineHeight:1,
              userSelect:"none", pointerEvents:"none", color:"var(--purple-text)",
            }}>🎵</div>
            <div style={{
              position:"absolute", right:"48px", bottom:"-14px",
              fontSize:"3.8rem", opacity:.04, lineHeight:1,
              userSelect:"none", pointerEvents:"none", color:"var(--purple-text)",
            }}>🎶</div>

            {/* Section label */}
            <div style={{
              fontSize:".6rem", fontWeight:"800", color:"#7C3AED",
              letterSpacing:"1.2px", textTransform:"uppercase", marginBottom:"12px",
              display:"flex", alignItems:"center", gap:"5px",
            }}>
              <span>🎓</span> {t("home.myCurrentCourse")}
            </div>

            {!course ? (
              <p style={{ fontSize:".88rem", color:"var(--text-muted)", margin:"0 0 12px" }}>
                {t("home.noCourseAssigned")}
              </p>
            ) : (
              <>
                {/* Course hero row */}
                <div style={{ display:"flex", alignItems:"center",
                              gap:"12px", marginBottom:"10px" }}>
                  {/* Big emoji badge */}
                  <div style={{
                    width:"52px", height:"52px", flexShrink:0,
                    background:"linear-gradient(135deg,#FBF0E8,#F5E4D0)",
                    borderRadius:"14px", display:"flex",
                    alignItems:"center", justifyContent:"center",
                    fontSize:"1.9rem",
                    boxShadow:"0 2px 8px rgba(123,21,21,.15)",
                  }}>
                    {subjectEmoji(course.subject)}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{
                      fontWeight:"800", fontSize:"1.02rem", color:"var(--text)",
                      whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                      lineHeight:1.25,
                    }}>
                      {translateCourseName(course, i18n.language)}
                    </div>
                    {course.description && (
                      <div style={{
                        fontSize:".74rem", color:"var(--text-muted)", marginTop:"3px",
                        overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis",
                      }}>
                        {course.description}
                      </div>
                    )}
                  </div>
                  {/* Courses-done badge — tap to open history sheet */}
                  {history.length > 0 && (
                    <div onClick={() => setShowHistory(true)} style={{
                      flexShrink:0, background:"#FEF3C7", borderRadius:"10px",
                      padding:"4px 9px", textAlign:"center", cursor:"pointer",
                    }}>
                      <div style={{ fontWeight:"800", fontSize:".95rem", color:"#D97706" }}>
                        {history.length}
                      </div>
                      <div style={{ fontSize:".55rem", color:"#D97706",
                                    lineHeight:1.1, whiteSpace:"nowrap" }}>
                        {t("common.done")}
                      </div>
                    </div>
                  )}
                </div>

                {/* Batch strip */}
                <div style={{
                  background:"var(--surface-2,linear-gradient(90deg,#F9F5FF,#FBF0E8))",
                  borderRadius:"10px", padding:"8px 12px",
                  display:"flex", alignItems:"center", gap:"8px",
                  marginBottom:"12px",
                  border:"1px solid var(--border-warm,#F5E4D0)",
                }}>
                  <span style={{ fontSize:".9rem" }}>🕐</span>
                  <div style={{ minWidth:0, flex:1 }}>
                    <span style={{
                      fontWeight:"700", fontSize:".82rem", color:"var(--purple-text)",
                      whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                    }}>
                      {batch?.name || t("home.noBatchAssigned")}
                    </span>
                    {batch?.timing && (
                      <span style={{
                        fontSize:".78rem", color:"var(--text-muted)", marginLeft:"8px",
                      }}>
                        {batch.timing}
                      </span>
                    )}
                  </div>
                </div>

                {/* Open button — full width CTA */}
                <button
                  className="btn btn-primary"
                  style={{ width:"100%", justifyContent:"center",
                           fontSize:".88rem", padding:"10px",
                           background:"linear-gradient(90deg,#7B1515,#8B3FCC)",
                           borderRadius:"10px", fontWeight:"700",
                           boxShadow:"0 3px 10px rgba(123,21,21,.25)",
                           letterSpacing:".2px",
                  }}
                  onClick={() => navigate(`/course/${student.courseId}`,
                    { state:{ batchId:student.batchId||"", isCompleted:false } })}>
                  {t("home.openMyCourse")}
                </button>
              </>
            )}
          </div>

          {/* ── Trivia (flex:6) + Footer (flex:4) share remaining space ── */}
          <TriviaCard
            fact={currentFact}
            index={displayIndex}
            total={triviaList.length}
            onPrev={prevTrivia}
            onNext={nextTrivia}
            visible={triviaVisible}
            compact
          />

          <ContactFooter compact/>
        </div>
      </Layout>
    );
  }

  // ── DESKTOP VIEW ─────────────────────────────────────────────────────────────
  // Layout:
  //   Stats row (3 stat cards)
  //   2-col: Course card | Trivia card
  //   Contact footer (full width, dark)
  //   Course history (if any — below, acceptable to scroll on desktop)
  return (
    <Layout title={t("home.title")} student={student}>
      {toastEl}

      {/* Stats row */}
      <div className="stats-grid" style={{ marginBottom:"18px" }}>
        <div className="stat-card">
          <div className="stat-val">
            {subjectEmoji(course?.subject)} {course?.name || "—"}
          </div>
          <div className="stat-lbl">{t("home.currentCourse")}</div>
        </div>
        <div className="stat-card" style={{ borderColor:"#16A34A" }}>
          <div className="stat-val" style={{ color:"#16A34A" }}>
            {batch?.name || "—"}
          </div>
          <div className="stat-lbl">{batch?.timing || t("home.currentBatch")}</div>
        </div>
        <div className="stat-card" style={{ borderColor:"#F59E0B" }}>
          <div className="stat-val" style={{ color:"#F59E0B" }}>
            {history.length}
          </div>
          <div className="stat-lbl">{t("home.coursesCompleted")}</div>
        </div>
      </div>

      {/* 2-col: course + trivia */}
      <div className="two-col" style={{
        display:"grid", gridTemplateColumns:"1fr 1fr",
        gap:"18px", marginBottom:"18px",
      }}>
        {/* Current course card */}
        <div className="card" style={{ margin:0 }}>
          <div className="card-header">
            <span className="card-title">🎓 {t("home.myCurrentCourse")}</span>
            {course && (
              <button className="btn btn-primary btn-sm"
                onClick={() => navigate(`/course/${student.courseId}`,
                  { state:{ batchId:student.batchId, isCompleted:false } })}>
                {t("home.openCourse")}
              </button>
            )}
          </div>
          {!course ? (
            <div className="empty-state" style={{ padding:"28px" }}>
              <div className="icon">📚</div>
              <p>{t("home.noCourseAssigned")}</p>
            </div>
          ) : (
            <div>
              <div style={{ display:"flex", alignItems:"center",
                            gap:"14px", marginBottom:"14px" }}>
                <span style={{ fontSize:"2.8rem" }}>
                  {subjectEmoji(course.subject)}
                </span>
                <div>
                  <div style={{ fontWeight:"700", fontSize:"1.02rem" }}>
                    {translateCourseName(course, i18n.language)}
                  </div>
                  {course.description && (
                    <div className="muted" style={{ marginTop:"3px" }}>
                      {course.description}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ background:"var(--surface-2,#F9F5FF)", borderRadius:"8px",
                            padding:"10px 14px" }}>
                <div style={{ fontSize:".76rem", fontWeight:"700",
                              color:"var(--purple-text)", marginBottom:"4px" }}>
                  {t("home.batchDetails")}
                </div>
                <div style={{ fontSize:".88rem", color:"var(--text-label)" }}>
                  🕐 {batch?.name || "—"}
                  {batch?.timing && ` (${batch.timing})`}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Trivia card */}
        <TriviaCard
          fact={currentFact}
          index={displayIndex}
          total={triviaList.length}
          onPrev={prevTrivia}
          onNext={nextTrivia}
          visible={triviaVisible}
          compact={false}
        />
      </div>

      {/* Contact footer — full width, dark */}
      <ContactFooter compact={false}/>

      {/* Course history — below footer, scrollable on desktop */}
      {history.length > 0 && (
        <div className="card" style={{ marginTop:"18px" }}>
          <div className="card-header">
            <span className="card-title">📜 {t("home.courseHistory")}</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("home.course")}</th>
                  <th>{t("home.batch")}</th>
                  <th>{t("home.completed")}</th>
                  <th>{t("home.notes")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {[...history].reverse().map((h, i) => (
                  <tr key={i}>
                    <td><div style={{ fontWeight:"600" }}>{h.courseName||"—"}</div></td>
                    <td className="muted">{h.batchName||"—"}</td>
                    <td><span className="badge badge-green">✅ {h.promotedAt||"—"}</span></td>
                    <td className="muted">{h.notes||"—"}</td>
                    <td>
                      <button className="btn btn-outline btn-sm"
                        onClick={() => navigate(`/course/${h.courseId}`,
                          { state:{ batchId:h.batchId, isCompleted:true } })}>
                        {t("home.viewHistory")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Layout>
  );
}
